import React, { useState, useEffect } from "react";
import { PRICE_POINT, GAIN_LOSS, RISK_LEVEL, SAFE_OR_GAMBLE } from "./App";
import { convertToDollars } from "./Question";
import { ResultCategory } from "./ResultCategory";
import ShareButton from "./ShareButton";
import { Archetype } from "./Archetype";
import { ResultPageInfoBox } from "./ResultPageInfoBox";
import { ref, push, onValue } from "firebase/database";
import { saveQuizResult, calculateAllTimeAverages } from "./cookieUtils";

function calculateData(answers) {
  // we want to calculate risk seeking / aversion (med / high / super high / too high)
  // ---- % of time that user gambles
  // loss aversion (gain / loss)
  // ---- % of safe bets in lossess vs gains (2x+ more losses; 2-1x more losses; 1-.3x more losses; .3-.3x; .3-1x more gains; 1-2x more gains; 2x+ more gains)
  // magnitude (5 levels)
  // ---- difference between high mag and low mag.
  // ---- low mag betting = 2x # bets in smallest mag + 1x # bets in second smallest mag
  // ---- high mag betting = 2x # bets in biggest mag + 1x # bets in second biggest mag

  let num_gambles = 0;
  let num_safe_bets_losses = 0;
  let num_safe_bets_gains = 0;
  let num_low_mag_bets = 0;
  let num_high_mag_bets = 0;
  let num_questions = 0;

  Object.values(GAIN_LOSS).forEach((gainLoss) => {
    Object.values(PRICE_POINT).forEach((pricePoint) => {
      Object.values(RISK_LEVEL).forEach((riskLevel) => {
        const result = answers[gainLoss][pricePoint][riskLevel];
        if (result !== null) {
          num_questions += 1;
          if (result === SAFE_OR_GAMBLE.GAMBLE) {
            num_gambles += 1;
            switch (pricePoint) {
              case PRICE_POINT.ONE:
                num_low_mag_bets += 1;
                break;
              case PRICE_POINT.TEN:
                num_low_mag_bets += 1;
                break;
              case PRICE_POINT.HUNDRED:
                break;
              case PRICE_POINT.TEN_THOUSAND:
                num_high_mag_bets += 1;
                break;
              case PRICE_POINT.MILLION:
                num_high_mag_bets += 2;
                break;
              default:
                break;
            }
          } else {
            if (gainLoss === GAIN_LOSS.LOSS) {
              num_safe_bets_losses += 1;
            } else {
              num_safe_bets_gains += 1;
            }
          }
        }
      });
    });
  });

  const gamble_percentage = num_gambles / num_questions;
  let loss_safe_percentage = 0.5;
  if (num_safe_bets_losses + num_safe_bets_gains !== 0) {
    loss_safe_percentage =
      num_safe_bets_gains / (num_safe_bets_losses + num_safe_bets_gains);
  }
  let magnitude_sensitivity = 0.5;
  if (num_high_mag_bets + num_low_mag_bets !== 0) {
    magnitude_sensitivity =
      num_low_mag_bets / (num_high_mag_bets + num_low_mag_bets);
  }
  return [gamble_percentage, loss_safe_percentage, magnitude_sensitivity];
}

function riskSeekingOutput(riskSeeking) {
  let subtitle = "";
  let value = 0;
  let share_text = "";
  if (riskSeeking >= 80) {
    subtitle = "Very risky";
    share_text = "Very risk seeking";
    value = 5;
  } else if (riskSeeking >= 60) {
    subtitle = "Somewhat risky";
    share_text = "Somewhat risk seeking";
    value = 4;
  } else if (riskSeeking >= 40) {
    subtitle = "Balanced";
    share_text = "Balanced in risk seeking";
    value = 3;
  } else if (riskSeeking >= 20) {
    subtitle = "Somewhat cautious";
    share_text = "Somewhat risk averse";
    value = 2;
  } else {
    subtitle = "Very cautious";
    share_text = "Very risk averse";
    value = 1;
  }

  return ["risk tolerance", subtitle, value, share_text];
}

function lossAversionOutput(lossAversion) {
  let subtitle = "";
  let value = 0;
  let share_text = "";
  if (lossAversion >= 80) {
    subtitle = "Very sensitive";
    share_text = "Very loss averse";
    value = 5;
  } else if (lossAversion >= 60) {
    subtitle = "Somewhat sensitive";
    share_text = "Somewhat loss averse";
    value = 4;
  } else if (lossAversion >= 40) {
    subtitle = "Balanced";
    share_text = "Balanced in loss aversion";
    value = 3;
  } else if (lossAversion >= 20) {
    subtitle = "Somewhat insensitive";
    share_text = "Somewhat loss seeking";
    value = 2;
  } else {
    subtitle = "Very insensitive";
    share_text = "Very loss seeking";
    value = 1;
  }

  return ["loss aversion", subtitle, value, share_text];
}

function magnitudeOutput(magnitude) {
  let subtitle = "";
  let value = 0;
  let share_text = "";
  if (magnitude >= 80) {
    subtitle = "Very sensitive";
    share_text = "Very magnitude sensitive";
    value = 5;
  } else if (magnitude >= 60) {
    subtitle = "Somewhat sensitive";
    share_text = "Somewhat magnitude sensitive";
    value = 4;
  } else if (magnitude >= 40) {
    subtitle = "Balanced";
    share_text = "Balanced in magnitude sensitivity";
    value = 3;
  } else if (magnitude >= 20) {
    subtitle = "Somewhat insensitive";
    share_text = "Somewhat magnitude insensitive";
    value = 2;
  } else {
    subtitle = "Very insensitive";
    share_text = "Very magnitude insensitive";
    value = 1;
  }

  return ["magnitude sensitivity", subtitle, value, share_text];
}

function getRiskArchetype(riskSeekingVal, lossAversionVal, magnitudeVal) {
  let role = "Role";
  let description = "description";
  let img = "img";
  if (riskSeekingVal < 4 && lossAversionVal > 3 && magnitudeVal > 2) {
    role = "The Scaredy Cat";
    description =
      "You prize comfort and safety above all else. Your favorite activities include staying home and watching TV, staying home and cooking dinner, and staying home and tidying up. Sure, you'll leave the house every once in a while, but as soon as you sense a threat - or anything resembling it - you retreat to your comfort zone. Sure, you might never see the seven wonders of the world, but you'll take what you know ten times out of ten.";
    img = "./cat.jpg";
  } else if (
    riskSeekingVal === 3 &&
    lossAversionVal === 3 &&
    magnitudeVal === 3
  ) {
    role = "The True Jedi";
    description =
      "You are one with the force. You have but to close your eyes to feel the balance in all things, including financial decisions. High risk or low risk? It doesn't matter. Loss or gain? You don't care. High magnitude? Magnitude is nothing on a cosmic scale. All things are equal to you, the Jedi Knight, as you float through life in perfect harmony with the order of the universe.";
    img = "./jedi.jpg";
  } else if (
    (riskSeekingVal !== 3 && lossAversionVal > 2 && magnitudeVal < 3) ||
    (riskSeekingVal === 3 && lossAversionVal > 3 && magnitudeVal < 3)
  ) {
    role = "The High Roller";
    description =
      "High stakes don't phase you. A million dollar gamble? That's what you call Wednesday. You have a lot of money, or act like you do. A poker game is the same, whether the buy-in is a dollar or a million - either way, a blue chip is a blue chip.";
    img = "./highroller.jpg";
  } else if (
    riskSeekingVal === 3 &&
    lossAversionVal === 3 &&
    magnitudeVal < 3
  ) {
    role = "The Logician";
    description =
      "Your best friend is your abacus. You hold a degree in a scientific field. You find work as an accountant, market analyst, or other flavor of bean-counting financial underling. You are able to flawlessly balance risks, and you understand intuitively that the only difference between losses and gains is the cnotext in which they appear. You do care about the size of the bet you place - your marginal utility is decreasing, after all - but other than that, you count yourself as one of the lucky few who can make sense of the world of dollars and cents.";
    img = "./logician.jpg";
  } else if (riskSeekingVal > 3 && magnitudeVal > 2) {
    role = "The Trigger Puller";
    description =
      "You don't hesitate. At the sound of a rustle in the bushes, your pistol is already out, ready to shoot from the hip. You're known around town as the fastest draw in the west. You like roller coasters, white water rafting, and fast cars. You're thinking about getting into BASE jumping. Who cares what the stakes might be? The thrill is what matters.";
    img = "./triggerpuller.jpg";
  } else if (lossAversionVal < 3 && magnitudeVal < 3) {
    role = "The Contradiction";
    description =
      "Your swimsuit is a pair of jeans. You drink coffee with a straw. You pack a ham sandwich when you go to the grocery store and wear shoes to bed. You don't know up from down, and you wouldn't want to, even if they paid you. You're the exception to every rule, and you're proud of it. Sometimes you sign up for scientific studies at the local university just to skew their results.";
    img = "./contradiction.jpg";
  } else if (riskSeekingVal < 4 && lossAversionVal < 3 && magnitudeVal > 2) {
    role = "The Microgambler";
    description =
      "You're in for poker - but only with the pennies cluttering the bottom of your wallet that you weren't gonna spend, anyway. You do enjoy the thrill of a small-stakes game, but as soon as the bidding gets high, you're out. You like cycling - but not mountain biking. You ski - but always wear your helmet. You like travel - but keep your wallet in a fanny pack. You'll explor life at your own safe, comforting pace.";
    img = "./microgambler.jpg";
  } else if (
    riskSeekingVal === 3 &&
    lossAversionVal === 3 &&
    magnitudeVal > 3
  ) {
    role = "The Pedestrian";
    description =
      "All you've ever wanted is a picket fence, a dog, two kids, and to plug away at the same job for decades. Sure, you'll put some money in the S&P 500, and you'll read up to make sure to balance your bonds and stocks every year, but beyond that, you don't touch the stuff. You just want to live your life and get by - is that too much to ask? You're just here to collect your paycheck and make it home in time for dinner, thank you very much.";
    img = "./pedestrian.jpg";
  } else if (riskSeekingVal < 3 && lossAversionVal === 3 && magnitudeVal > 2) {
    role = "The Good Christian";
    description =
      "Gambling is a sin. If Satan ever hoodwinked you into dabbling in that dark art, you'd be back in church as fast as an answered prayer, ready to recount your malfeasance in confession. It took you all of thirty seconds to take this quiz. Your worldview helps divide everything you encounter into distinct shapes, and these demonic temptations clearly fall beyond the line. You'll stick to your knitting, abstinence, and sobriety, thank you very much.";
    img = "christian.jpg";
  }
  return [role, description, img];
}

function getPercentile(arr, val) {
  const index = arr.indexOf(val);
  if (index === -1) {
    // value not found, interpolate between nearest values
    const lowerIndex = findLowerIndex(arr, val);
    const upperIndex = findUpperIndex(arr, val);
    const lowerVal = arr[lowerIndex];
    const upperVal = arr[upperIndex];
    const interpolatedPercentile =
      ((val - lowerVal) / (upperVal - lowerVal)) * (upperIndex - lowerIndex) +
      lowerIndex;
    const percentileRank = Math.round(
      (interpolatedPercentile / arr.length) * 100
    );
    return [`p${percentileRank} of users`, percentileRank];
  } else {
    const percentileRank = Math.round((index / arr.length) * 100);
    return [`p${percentileRank} of users`, percentileRank];
  }
}
function findLowerIndex(arr, val) {
  let index = 0;
  while (arr[index] < val) {
    index++;
  }
  return index - 1;
}
function findUpperIndex(arr, val) {
  let index = arr.length - 1;
  while (arr[index] > val) {
    index--;
  }
  return index + 1;
}

function getPercentiles(
  riskSeekingVal,
  lossAversionVal,
  magnitudeVal,
  snapshot
) {
  const userData = snapshot.val();
  const riskSeekingVals = [];
  const lossAversionVals = [];
  const magnitudeVals = [];
  Object.keys(userData).forEach((key) => {
    riskSeekingVals.push(userData[key].riskSeekingVal);
    lossAversionVals.push(userData[key].lossAversionVal);
    magnitudeVals.push(userData[key].magnitudeVal);
  });
  riskSeekingVals.sort((a, b) => a - b);
  lossAversionVals.sort((a, b) => a - b);
  magnitudeVals.sort((a, b) => a - b);
  const riskSeekingPercentile = getPercentile(riskSeekingVals, riskSeekingVal);
  const lossAversionPercentile = getPercentile(
    lossAversionVals,
    lossAversionVal
  );
  const magnitudePercentile = getPercentile(magnitudeVals, magnitudeVal);
  return [
    riskSeekingPercentile[0],
    lossAversionPercentile[0],
    magnitudePercentile[0],
    riskSeekingPercentile[1],
    lossAversionPercentile[1],
    magnitudePercentile[1],
  ];
}

function getSnapshot(database) {
  const userDataRef = ref(database, "user_data");
  return new Promise((resolve, reject) => {
    onValue(
      userDataRef,
      (snapshot) => {
        resolve(snapshot);
      },
      (error) => {
        reject(error);
      }
    );
  });
}

const Results = ({ answers, betTotal, database }) => {
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [popupText, setPopupText] = useState("");
  const [titleText, setTitleText] = useState("");
  const output_data = calculateData(answers);
  const [riskSeeking, setRiskSeeking] = useState(0);
  const [lossAversion, setLossAversion] = useState(0);
  const [magnitude, setMagnitude] = useState(0);
  const [allTimeStats, setAllTimeStats] = useState(null);
  const [allTimePercentiles, setAllTimePercentiles] = useState([0, 0, 0, 0, 0, 0]);
  const earnings = convertToDollars(betTotal);
  const earned_money = betTotal >= 0;
  const [role, description, img] = getRiskArchetype(
    riskSeeking[2],
    lossAversion[2],
    magnitude[2]
  );
  useEffect(() => {
    getSnapshot(database)
      .then((snapshot) => {
        const percentiles = getPercentiles(
          output_data[0],
          output_data[1],
          output_data[2],
          snapshot
        );
        // percentiles 0, 1, 2 is text and 3, 4, 5 is number
        setRiskSeeking(riskSeekingOutput(percentiles[3]));
        setLossAversion(lossAversionOutput(percentiles[4]));
        setMagnitude(magnitudeOutput(percentiles[5]));
        document.getElementById(
          "risk-seeking-percentile"
        ).textContent = `${percentiles[0]}`;
        document.getElementById(
          "loss-aversion-percentile"
        ).textContent = `${percentiles[1]}`;
        document.getElementById(
          "magnitude-percentile"
        ).textContent = `${percentiles[2]}`;
        document.getElementById(
          "risk-seeking-percentile-dot"
        ).style.left = `${percentiles[3]}%`;
        document.getElementById(
          "loss-aversion-percentile-dot"
        ).style.left = `${percentiles[4]}%`;
        document.getElementById(
          "magnitude-percentile-dot"
        ).style.left = `${percentiles[5]}%`;
        
        // Save current results to cookies and calculate all-time averages
        const history = saveQuizResult(percentiles[3], percentiles[4], percentiles[5], 20, betTotal);
        const allTimeAverages = calculateAllTimeAverages(history);
        setAllTimeStats(allTimeAverages);
        
        // Calculate percentiles for all-time averages using the same data as current results
        const calculatedAllTimePercentiles = getPercentiles(
          allTimeAverages.riskTolerance / 100, // Convert back to 0-1 scale for percentile calculation
          allTimeAverages.lossAversion / 100,
          allTimeAverages.magnitudeSensitivity / 100,
          snapshot
        );
        setAllTimePercentiles(calculatedAllTimePercentiles);
        
        // Set dot positions and percentile text for all-time averages after component mounts
        setTimeout(() => {
          const riskElement = document.getElementById("all-time-risk-seeking-percentile-dot");
          const lossElement = document.getElementById("all-time-loss-aversion-percentile-dot");
          const magnitudeElement = document.getElementById("all-time-magnitude-percentile-dot");
          
          if (riskElement) riskElement.style.left = `${calculatedAllTimePercentiles[3]}%`;
          if (lossElement) lossElement.style.left = `${calculatedAllTimePercentiles[4]}%`;
          if (magnitudeElement) magnitudeElement.style.left = `${calculatedAllTimePercentiles[5]}%`;
          
          // Set percentile text to show percentiles like current results
          const riskPercentileElement = document.getElementById("all-time-risk-seeking-percentile");
          const lossPercentileElement = document.getElementById("all-time-loss-aversion-percentile");
          const magnitudePercentileElement = document.getElementById("all-time-magnitude-percentile");
          
          if (riskPercentileElement) riskPercentileElement.textContent = calculatedAllTimePercentiles[0];
          if (lossPercentileElement) lossPercentileElement.textContent = calculatedAllTimePercentiles[1];
          if (magnitudePercentileElement) magnitudePercentileElement.textContent = calculatedAllTimePercentiles[2];
        }, 100);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  // save to the database
  const userDataRef = ref(database, "user_data");
  const userData = {
    riskSeekingVal: output_data[0],
    lossAversionVal: output_data[1],
    magnitudeVal: output_data[2],
    betTotal: betTotal,
    timestamp: Date.now(),
  };
  push(userDataRef, userData);

  const riskSeekingText = (
    <>
      <p>
        Most people are{" "}
        <a href="https://en.wikipedia.org/wiki/Risk_aversion">risk averse</a>,
        meaning that they prefer sure things to uncertain bets, even when the
        expected payoff is the same. <b>Cautious</b> people prefer guaranteed
        payouts even more than the average person. <b>Risky</b> people have a
        higher than usual tolerance for gambling and risk taking. People who are{" "}
        <b>balanced</b> are somewhere in the middle, and within the average
        range.
      </p>
      <p>
        In parentheses is your percentile. For example, if it says (p45 of
        users), that means you are more risk seeking than 45% of users of this
        site.
      </p>
    </>
  );
  const lossAversionText = (
    <>
      <p>
        Most people are{" "}
        <a href="https://en.wikipedia.org/wiki/Prospect_theory">
          risk seeking in losses
        </a>
        , meaning that they are more likely to take risks when losing money than
        when gaining money. People who are <b>sensitive</b> to loss aversion are
        even more likely to prefer gambling when losing money (as opposed to
        when winning money). People who are <b>insensitive</b> to loss aversion
        are the opposite: they gamble more often when they stand to gain money.
        People who are <b>balanced</b> are somewhere in the middle, and within
        the average range.
      </p>
      <p>
        In parentheses is your percentile. For example, if it says (p45 of
        users), that means you are more risk seeking in losses than 45% of users
        of this site.
      </p>
    </>
  );
  const magnitudeText = (
    <>
      <p>
        Most people experience{" "}
        <a href="https://en.wikipedia.org/wiki/Marginal_utility">
          decreasing marginal utility
        </a>
        , meaning that the first thousand dollars they earn is more important to
        them than the next thousand dollars they earn. People who are{" "}
        <b>sensitive</b> to magnitude are even more this way, which results in
        them taking safer bets with larger amounts of money present. Those who
        are <b>insensitive</b> are the opposite: the more money on the table,
        the more likely they are to gamble. Those who are <b>balanced</b> are
        somewhere in the middle, and within the average range.
      </p>
      <p>
        In parentheses is your percentile. For example, if it says (p45 of
        users), that means you are more magnitude sensitive than 45% of users of
        this site.
      </p>
    </>
  );

  return (
    <div>
      <h2 className="page-header">Risk Profile</h2>
      <ResultCategory
        label={riskSeeking[0]}
        text={riskSeeking[1]}
        percentage=""
        percentileId={"risk-seeking-percentile"}
        dotId={"risk-seeking-percentile-dot"}
        onClickFunction={() => {
          setShowResultPopup(true);
          setPopupText(riskSeekingText);
          setTitleText(riskSeeking[0]);
        }}
      />
      <ResultCategory
        label={lossAversion[0]}
        text={lossAversion[1]}
        percentage=""
        percentileId={"loss-aversion-percentile"}
        dotId={"loss-aversion-percentile-dot"}
        onClickFunction={() => {
          setShowResultPopup(true);
          setPopupText(lossAversionText);
          setTitleText(lossAversion[0]);
        }}
      />
      <ResultCategory
        label={magnitude[0]}
        text={magnitude[1]}
        percentage=""
        percentileId={"magnitude-percentile"}
        dotId={"magnitude-percentile-dot"}
        onClickFunction={() => {
          setShowResultPopup(true);
          setPopupText(magnitudeText);
          setTitleText(magnitude[0]);
        }}
      />
      <Archetype role={role} description={description} img={img} />
      <div>
        <div className={`earnings unlocked`}>
          <p>
            <span>Total winnings:&nbsp; </span>
            <span
              className={`${betTotal > 0 ? "unlocked-won" : "unlocked-lost"}`}
            >
              {" "}
              {convertToDollars(betTotal)}
            </span>
          </p>
        </div>
      </div>
      <ShareButton
        riskSeeking={riskSeeking}
        lossAversion={lossAversion}
        magnitude={magnitude}
        earnings={earnings}
        earned_money={earned_money}
        role={role}
        use_navigator_share={true}
      />
      <br />
      <ShareButton
        riskSeeking={riskSeeking}
        lossAversion={lossAversion}
        magnitude={magnitude}
        earnings={earnings}
        earned_money={earned_money}
        role={role}
        use_navigator_share={false}
      />
      {allTimeStats && (
        <div style={{ marginTop: '40px' }}>
          <h2 className="page-header">All-Time Results</h2>
          <p style={{ textAlign: 'center', marginTop: '5px', marginBottom: '20px', fontSize: '1.1em' }}>
            Based on {allTimeStats.totalBets} bets across {allTimeStats.totalQuizzes} quiz{allTimeStats.totalQuizzes > 1 ? 'es' : ''}
          </p>
          <ResultCategory
            label="risk tolerance"
            text={riskSeekingOutput(allTimePercentiles[3])[1]}
            percentage={allTimePercentiles[3]}
            percentileId={"all-time-risk-seeking-percentile"}
            dotId={"all-time-risk-seeking-percentile-dot"}
            onClickFunction={() => {
              setShowResultPopup(true);
              setPopupText(riskSeekingText);
              setTitleText("risk tolerance");
            }}
          />
          <ResultCategory
            label="loss aversion"
            text={lossAversionOutput(allTimePercentiles[4])[1]}
            percentage={allTimePercentiles[4]}
            percentileId={"all-time-loss-aversion-percentile"}
            dotId={"all-time-loss-aversion-percentile-dot"}
            onClickFunction={() => {
              setShowResultPopup(true);
              setPopupText(lossAversionText);
              setTitleText("loss aversion");
            }}
          />
          <ResultCategory
            label="magnitude sensitivity"
            text={magnitudeOutput(allTimePercentiles[5])[1]}
            percentage={allTimePercentiles[5]}
            percentileId={"all-time-magnitude-percentile"}
            dotId={"all-time-magnitude-percentile-dot"}
            onClickFunction={() => {
              setShowResultPopup(true);
              setPopupText(magnitudeText);
              setTitleText("magnitude sensitivity");
            }}
          />
          <div className={`earnings unlocked`} style={{ marginTop: '20px' }}>
            <p>
              <span>All-time winnings:&nbsp; </span>
              <span
                className={`${allTimeStats.totalWinnings > 0 ? "unlocked-won" : "unlocked-lost"}`}
              >
                {" "}
                {convertToDollars(allTimeStats.totalWinnings)}
              </span>
            </p>
          </div>
        </div>
      )}
      <br />
      <button
        onClick={() => window.location.reload()}
        className="quiz-again-button"
      >
        ↻ Take quiz again
      </button>
      {showResultPopup && (
        <ResultPageInfoBox
          onClose={() => setShowResultPopup(false)}
          infoText={popupText}
          titleText={titleText}
        />
      )}
    </div>
  );
};

export default Results;
